import React, { useEffect, useState, useMemo, useCallback } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import ButtonToolbar from "react-bootstrap/ButtonToolbar";
import { Formik } from "formik";
import * as yup from "yup";
import "./template-creation.scss";
import {
  CreateProjectTemplateAPI,
  EditProjectTemplateAPI,
} from "../../../../../services/api/project-template-api";
import AdminDynamicFieldsTemplate from "../../task-types/dynamic-fields/dynamic-fields-panel/components/admin-dynamic-fields-template";
import { saveDynamicFields } from "../../task-types/dynamic-fields/utils/admin-dynamic-fields-util";
import TemplateCreationFormLeftNew from "./template-creation-form-left-new";
import TemplateCreationFormRightNew from "./template-creation-form-right-new";
import {
  InitializeForm,
  PrepareModifyTemplate,
  isDynamicFieldsInvalid,
} from "../template-utility";
import MultiOwnerConfirmationModal from "./multi-owner-confirmation-modal";
import PropTypes from "prop-types";

const TemplateCreationFormNew = ({
  template,
  isCreate,
  setAlertConfig,
  setPanelVisibility,
  dynamicFields,
  isView,
  isEdit,
  objectTypes,
  setDynamicFields,
  setTemplate,
  departments,
  selectedDepartment,
  setSelectedDepartment,
  users,
  updateOwners,
  updateMembers,
  isSuperAdmin,
  handleDepartmentChange
}) => {
  const [checked, setChecked] = useState(false);
  const [showMultiOwnerModal, setShowMultiOwnerModal] = useState(false);
  const [initialMultiOwner, setInitialMultiOwner] = useState(template.data?.IsMultiOwner);
  const [multiOwnerToggledOnThisSession, setMultiOwnerToggledOnThisSession] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const [pendingFormActions, setPendingFormActions] = useState(null);

  const handleMultiOwnerToggle = (isToggledOn) => {
    if (isToggledOn && !initialMultiOwner) {
      setMultiOwnerToggledOnThisSession(true);
    }
  };

  useEffect(() => {
    setInitialMultiOwner(template.data?.IsMultiOwner);
    setMultiOwnerToggledOnThisSession(false);
  }, [template.data?.Id, template.data?.IsMultiOwner]);

  const CreateAndUpdateProject = useCallback(
    async (templateForm) => {
      try {
        let templateRequest = PrepareModifyTemplate(templateForm, isCreate);
        let result = isCreate
          ? await CreateProjectTemplateAPI(templateRequest)
          : await EditProjectTemplateAPI(templateRequest);

        if (result?.response?.status === 200 || result?.status === 200) {
          let templateId = isCreate ? result.data.Id : templateRequest.Id;

          await saveDynamicFields(0, templateId, dynamicFields);

          setTemplate((prev) => ({
            ...prev,
            data: {
              ...prev.data,
              ProjectType: templateForm.ProjectType,
              Id: templateId,
            },
          }));
          setPanelVisibility((prev) => ({
            ...prev,
            showConfirmPanel: !prev.showConfirmPanel,
          }));
        } else {
          throw new Error(result.response.data);
        }
      } catch (error) {
        setAlertConfig({
          showAlert: true,
          alertAction: isEdit
            ? "Cannot Edit Template"
            : "Cannot Create Template",
          alertMessage: error.toString(),
          alertVariant: "danger",
        });
        setTemplate((prev) => ({ ...prev, isLoading: !prev.isLoading }));
        setChecked((prev) => !prev);
      }
    },
    [dynamicFields]
  );

  const handleFormSubmit = useCallback(
    async (values, actions) => {
      if (multiOwnerToggledOnThisSession && values?.IsMultiOwner) {
        setPendingValues(values);
        setPendingFormActions(actions);
        setShowMultiOwnerModal(true);
        return;
      }
      CreateAndUpdateProject(values, actions);
    },
    [multiOwnerToggledOnThisSession, CreateAndUpdateProject]
  );

  const handleMultiOwnerConfirm = useCallback(async () => {
    setShowMultiOwnerModal(false);
    if (pendingValues) {
      if (pendingValues?.IsMultiOwner) {
        setInitialMultiOwner(true);
        setMultiOwnerToggledOnThisSession(false);
      }
      await CreateAndUpdateProject(pendingValues, pendingFormActions || {});
      setPendingValues(null);
      setPendingFormActions(null);
    }
  }, [
    pendingValues,
    pendingFormActions,
    CreateAndUpdateProject,
    setInitialMultiOwner,
    setMultiOwnerToggledOnThisSession,
    setShowMultiOwnerModal,
  ]);

  const OnKeyDown = (keyEvent) => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
    }
  };

  const EnterKeySubmission = (keyEvent, submitForm) => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      submitForm();
    }
  };

  const EnterKeyCancel = (keyEvent) => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      setPanelVisibility((prev) => ({ ...prev, showCancelPanel: false }));
    }
  };

  return (
    <Formik
      key={template.data.Id}
      enableReinitialize={true}
      validationSchema={yup.object({
        ProjectType: yup.string().required("Project Name Required"),
        Days: yup
          .number()
          .typeError("Please select duration of more than 0")
          .required("Default Duration is required")
          .min(0, "Please select dates within the range of 0-365 days"),
        IsVisible: yup.boolean().required("Template Access required"),
      })}
      onSubmit={(values, actions) => {
        handleFormSubmit(values, actions);
      }}
      initialValues={useMemo(() => {
        return InitializeForm(template.data, isEdit, template.isLoading);
      }, [template.data.Id])}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
        setFieldValue,
        isSubmitting,
        submitForm,
        setFieldTouched,
        validateForm,
      }) => (
        <Form
          className="template-creation-container"
          onSubmit={handleSubmit}
          onKeyDown={OnKeyDown}
        >
          <div className="form-container">
            <TemplateCreationFormLeftNew
              values={values}
              errors={errors}
              handleChange={handleChange}
              handleBlur={handleBlur}
              touched={touched}
              isView={isView}
              setFieldValue={setFieldValue}
            />
            <TemplateCreationFormRightNew
              values={values}
              errors={errors}
              handleChange={handleChange}
              handleBlur={handleBlur}
              touched={touched}
              isView={isView}
              setFieldValue={setFieldValue}
              departments={departments}
              selectedDepartment={selectedDepartment}
              setSelectedDepartment={setSelectedDepartment}
              users={users}
              updateOwners={updateOwners}
              updateMembers={updateMembers}
              isSuperAdmin={isSuperAdmin}
              handleDepartmentChange={handleDepartmentChange}
              isEdit={isEdit}
              onMultiOwnerToggle={handleMultiOwnerToggle}
              initialMultiOwner={initialMultiOwner}
            />
          </div>

          <div className="admin-dynamic-fields-panel-wrapper">
            <AdminDynamicFieldsTemplate
              dynamicFields={dynamicFields}
              setDynamicFields={setDynamicFields}
              objectTypes={objectTypes}
              type={"projectTemplate"}
            />
          </div>

          <ButtonToolbar className="template-btn-container">
            {!isView && (
              <Button
                className="btn-wide"
                type="submit"
                onKeyDown={(keyEvent) =>
                  EnterKeySubmission(keyEvent, submitForm)
                }
                disabled={isDynamicFieldsInvalid(
                  dynamicFields,
                  checked,
                  isSubmitting
                )}
              >
                {isEdit ? "Save" : "Create"}
              </Button>
            )}

            <Button
              onKeyDown={(keyEvent) => EnterKeyCancel(keyEvent)}
              className="btn-wide btn-outlined"
              onClick={() => {
                setPanelVisibility((prev) => ({
                  ...prev,
                  showCancelPanel: true,
                }));
              }}
            >
              Cancel
            </Button>
          </ButtonToolbar>

          <MultiOwnerConfirmationModal open={showMultiOwnerModal} setOpen={setShowMultiOwnerModal} projectType={values?.ProjectType} onCancel={() => { if (pendingFormActions) { pendingFormActions.setSubmitting(false); } setPendingValues(null); setPendingFormActions(null); }} onConfirm={handleMultiOwnerConfirm} />
        </Form>
      )}
    </Formik>
  );
};

TemplateCreationFormNew.propTypes = {
  setAlertConfig: PropTypes.func.isRequired,
  setPanelVisibility: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  isView: PropTypes.bool.isRequired,
  template: PropTypes.object.isRequired,
  isCreate: PropTypes.bool.isRequired,
  dynamicFields: PropTypes.array.isRequired,
  objectTypes: PropTypes.func.isRequired,
  setDynamicFields: PropTypes.func.isRequired,
  setTemplate: PropTypes.func.isRequired,
  departments: PropTypes.array.isRequired,
  selectedDepartment: PropTypes.object,
  setSelectedDepartment: PropTypes.func.isRequired,
  users: PropTypes.array.isRequired,
  updateOwners: PropTypes.func.isRequired,
  updateMembers: PropTypes.func.isRequired,
  isSuperAdmin: PropTypes.bool.isRequired,
  handleDepartmentChange: PropTypes.func.isRequired,
};
export default TemplateCreationFormNew;
